import React, { useEffect, useRef } from 'react';
import { Progress, Button, Tooltip } from 'antd';
import { Chart, registerables } from 'chart.js';
import {
    RotateCw,
    Store,
    Tag,
    TrendingUp,
    TriangleAlert,
    CircleCheck,
    Users,
    Package,
    ScanBarcode,
    Layers,
    BadgePercent,
    ListChecks,
    Image as ImageIcon,
    ChartBar,
    Trophy,
} from 'lucide-react';
import { brand, semantic } from '../../../theme/tokens';
import { SectionCard, EmptyState, StatusTag } from '../../common';
import AuditKpiCard from './AuditKpiCard';
import { fmtVZ } from '../../../utils/timeUtils';

Chart.register(...registerables);

const GRID = '#eef0f3';
const TICKS = { color: '#6b7585', font: { size: 11 } };

function fmtNum(n) {
    const v = Number(n) || 0;
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return String(v);
}

function ActivityChart({ dailyTrend }) {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas || !dailyTrend?.length) return;
        if (canvas.chart) canvas.chart.destroy();

        const labels = dailyTrend.map((d) => {
            const [, m, day] = d.date.split('-');
            return `${day}/${m}`;
        });

        canvas.chart = new Chart(canvas, {
            type: 'line',
            data: {
                labels,
                datasets: [
                    {
                        label: 'Escaneos',
                        data: dailyTrend.map((d) => d.scans),
                        borderColor: brand[600],
                        backgroundColor: 'rgba(26, 99, 227, 0.08)',
                        fill: true,
                        tension: 0.35,
                        borderWidth: 2,
                        pointRadius: 2.5,
                    },
                    {
                        label: 'Visitas',
                        data: dailyTrend.map((d) => d.visits),
                        borderColor: semantic.info.solid,
                        backgroundColor: semantic.info.solid,
                        fill: false,
                        tension: 0.35,
                        borderWidth: 2,
                        pointRadius: 2.5,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, font: { size: 12 } } } },
                scales: {
                    y: { beginAtZero: true, ticks: { ...TICKS, precision: 0 }, grid: { color: GRID }, border: { display: false } },
                    x: { ticks: { ...TICKS, maxRotation: 0, autoSkip: true, maxTicksLimit: 7 }, grid: { display: false } },
                },
            },
        });

        return () => canvas.chart?.destroy();
    }, [dailyTrend]);

    return (
        <div style={{ height: 240 }}>
            <canvas ref={ref} />
        </div>
    );
}

function CategoryChart({ topCategories }) {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas || !topCategories?.length) return;
        if (canvas.chart) canvas.chart.destroy();

        canvas.chart = new Chart(canvas, {
            type: 'bar',
            data: {
                labels: topCategories.map((c) => c.category),
                datasets: [
                    {
                        label: 'Escaneos (30d)',
                        data: topCategories.map((c) => c.scans),
                        backgroundColor: brand[500],
                        borderRadius: 4,
                        borderSkipped: false,
                        maxBarThickness: 22,
                    },
                ],
            },
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    x: { beginAtZero: true, ticks: { ...TICKS, precision: 0 }, grid: { color: GRID }, border: { display: false } },
                    y: { ticks: TICKS, grid: { display: false } },
                },
            },
        });

        return () => canvas.chart?.destroy();
    }, [topCategories]);

    return (
        <div style={{ height: Math.max(180, topCategories.length * 36) }}>
            <canvas ref={ref} />
        </div>
    );
}

function ActivityFeed({ visits, onVisitClick }) {
    if (!visits?.length) return <EmptyState icon={<Store />} title="Sin visitas recientes" />;

    return (
        <ul className="m-0 p-0 list-none divide-y divide-surface-100">
            {visits.slice(0, 8).map((v) => (
                <li
                    key={v._id}
                    className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-surface-25 transition-colors"
                    onClick={() => onVisitClick?.(v)}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-info-bg text-info-fg text-[15px]">
                        <Store />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="truncate text-sm font-medium text-surface-900">{v.storeId?.name || 'Tienda'}</div>
                        <div className="truncate text-xs text-surface-500 tabular-nums">
                            {v.userId?.name || 'Auditor'} · {v.totalProducts ?? 0} SKUs · {fmtVZ(v.createdAt)}
                        </div>
                    </div>
                    <StatusTag tone="neutral" className="tabular-nums">
                        {v.totalProducts ?? 0}
                    </StatusTag>
                </li>
            ))}
        </ul>
    );
}

export function AuditorRankings({ auditors, onAuditorClick }) {
    if (!auditors?.length) return <EmptyState icon={<Users />} title="Sin auditores activos" />;

    const maxVisits = Math.max(...auditors.map((a) => a.visits || 0), 1);

    return (
        <ul className="m-0 p-0 list-none divide-y divide-surface-100">
            {auditors.slice(0, 8).map((a, idx) => (
                <li
                    key={String(a.userId)}
                    className="px-5 py-3 cursor-pointer hover:bg-surface-25 transition-colors"
                    onClick={() => onAuditorClick?.(a.userId)}>
                    <div className="flex items-center gap-3 mb-2">
                        <span
                            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${
                                idx === 0 ? 'bg-warning-bg text-warning-fg' : 'bg-surface-100 text-surface-600'
                            }`}>
                            {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div className="truncate text-sm font-medium text-surface-900">{a.user?.name || 'Auditor'}</div>
                            <div className="text-xs text-surface-500 tabular-nums">
                                {a.visits} visitas · {fmtNum(a.productsReported)} SKUs
                            </div>
                        </div>
                        <Tooltip title="Porcentaje de imágenes que fallaron el procesamiento">
                            <span className="inline-flex">
                                <StatusTag tone={a.errorRate > 15 ? 'danger' : 'success'} className="tabular-nums">
                                    {a.errorRate ?? 0}% img. fallidas
                                </StatusTag>
                            </span>
                        </Tooltip>
                    </div>
                    <div className="pl-10">
                        <Progress
                            percent={Math.round(((a.visits || 0) / maxVisits) * 100)}
                            showInfo={false}
                            strokeColor={brand[600]}
                            size="small"
                            className="!m-0"
                        />
                    </div>
                </li>
            ))}
        </ul>
    );
}

/** Quiet number tile for the catalog-health block. */
function HealthTile({ icon, tone, label, value }) {
    const chip = {
        warning: 'bg-warning-bg text-warning-fg',
        violet: 'bg-violet-bg text-violet-fg',
        info: 'bg-info-bg text-info-fg',
        success: 'bg-success-bg text-success-fg',
    }[tone];
    return (
        <div className="rounded-lg border border-surface-200 p-3">
            <div className="flex items-center gap-2">
                <span className={`flex h-6 w-6 items-center justify-center rounded-md text-[13px] ${chip}`}>{icon}</span>
                <span className="text-xs text-surface-500">{label}</span>
            </div>
            <div className="mt-1.5 text-lg font-semibold text-surface-900 tabular-nums">{value}</div>
        </div>
    );
}

export default function AuditAnalyticsPanel({
    analytics,
    loading,
    onRefresh,
    onVisitClick,
    onAuditorClick,
    navigateKpiFilter,
    onReviewQueueClick,
}) {
    if (loading && !analytics) return null;
    if (!analytics) return null;

    const quality = analytics.quality || {};
    const coverage = analytics.coverage || {};
    const barcodeErrors = quality.barcodeErrorsLast7Days || 0;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
                <p className="m-0 text-sm text-surface-500">Haz clic en un indicador para ir a su detalle.</p>
                <Button icon={<RotateCw />} loading={loading} onClick={onRefresh}>
                    Actualizar
                </Button>
            </div>

            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
                <AuditKpiCard
                    title="SKUs en catálogo"
                    value={fmtNum(analytics.auditProducts?.total ?? 0)}
                    icon={<Package />}
                    tone="brand"
                    onClick={() => navigateKpiFilter?.('products')}
                />
                <AuditKpiCard
                    title="PDVs auditados (30d)"
                    value={fmtNum(coverage.uniqueStoresLast30 ?? 0)}
                    icon={<Store />}
                    tone="info"
                    onClick={() => navigateKpiFilter?.('checks')}
                />
                <AuditKpiCard
                    title="Escaneos (7 días)"
                    value={fmtNum(analytics.auditLineItems?.last7Days ?? 0)}
                    icon={<ScanBarcode />}
                    tone="brand"
                    current={analytics.auditLineItems?.last7Days}
                    prior={analytics.auditLineItems?.prior7Days}
                    onClick={() => navigateKpiFilter?.('records')}
                />
                <AuditKpiCard
                    title="Prom. SKUs / visita"
                    value={coverage.avgProductsPerVisit ?? 0}
                    icon={<Layers />}
                    tone="success"
                    onClick={() => navigateKpiFilter?.('checks')}
                />
                <AuditKpiCard
                    title="Promos detectadas"
                    value={`${quality.promoRate ?? 0}%`}
                    icon={<BadgePercent />}
                    tone="violet"
                    onClick={() => navigateKpiFilter?.('records')}
                />
                <AuditKpiCard
                    title="Cola de revisión"
                    value={quality.reviewQueueCount ?? 0}
                    icon={<ListChecks />}
                    tone="warning"
                    onClick={onReviewQueueClick}
                />
            </div>

            <div className="grid gap-4 grid-cols-1 lg:grid-cols-12">
                <SectionCard title="Actividad de campo — últimos 14 días" icon={<TrendingUp />} className="lg:col-span-7">
                    <ActivityChart dailyTrend={analytics.dailyTrend || []} />
                </SectionCard>
                <SectionCard title="Salud del catálogo" icon={<CircleCheck />} className="lg:col-span-5">
                    <div className="space-y-5">
                        <div>
                            <div className="flex justify-between mb-1">
                                <span className="text-sm text-surface-600">Catálogo completo</span>
                                <span className="text-sm font-semibold text-surface-900 tabular-nums">{quality.catalogCompletePct ?? 0}%</span>
                            </div>
                            <Progress percent={quality.catalogCompletePct ?? 0} strokeColor={semantic.success.solid} showInfo={false} className="!m-0" />
                        </div>
                        <div>
                            <div className="flex justify-between mb-1">
                                <span className="text-sm text-surface-600">Calidad de códigos (7d)</span>
                                <span className={`text-sm font-semibold tabular-nums ${quality.barcodeErrorsLast7Days > 0 ? 'text-danger-fg' : 'text-success-fg'}`}>
                                    {quality.barcodeErrorsLast7Days ?? 0} errores
                                </span>
                            </div>
                            <Progress
                                percent={Math.max(0, 100 - Math.min(100, barcodeErrors * 2))}
                                strokeColor={quality.barcodeErrorsLast7Days > 10 ? semantic.danger.solid : brand[600]}
                                showInfo={false}
                                className="!m-0"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <HealthTile icon={<TriangleAlert />} tone="warning" label="Duplicados" value={quality.duplicateProducts ?? 0} />
                            <HealthTile icon={<Tag />} tone="violet" label="Promos (30d)" value={fmtNum(quality.promoScansLast30 ?? 0)} />
                            <HealthTile icon={<Store />} tone="info" label="Visitas (30d)" value={fmtNum(coverage.visitsLast30 ?? 0)} />
                            <HealthTile icon={<ImageIcon />} tone="success" label="Imágenes (30d)" value={fmtNum(coverage.totalImagesLast30 ?? 0)} />
                        </div>
                    </div>
                </SectionCard>
            </div>

            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                <SectionCard title="Categorías más auditadas (30d)" icon={<ChartBar />}>
                    {analytics.topCategories?.length ? (
                        <CategoryChart topCategories={analytics.topCategories} />
                    ) : (
                        <EmptyState icon={<ChartBar />} title="Sin datos por categoría" />
                    )}
                </SectionCard>
                <SectionCard title="Top SKUs escaneados (30d)" icon={<Trophy />} flush>
                    {analytics.topScannedProducts?.length ? (
                        <ul className="m-0 p-0 list-none divide-y divide-surface-100">
                            {analytics.topScannedProducts.map((p, idx) => (
                                <li key={p.productId || idx} className="flex items-center gap-3 px-5 py-2.5">
                                    <span className="w-5 text-center text-xs text-surface-400 tabular-nums">{idx + 1}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="truncate text-sm font-medium text-surface-900">{p.name}</div>
                                        <div className="text-xs text-surface-500 tabular-nums">
                                            {p.code} · {p.category || '—'}
                                        </div>
                                    </div>
                                    <span className="shrink-0 text-sm font-semibold text-surface-900 tabular-nums">
                                        {fmtNum(p.scans)} <span className="text-xs font-normal text-surface-400">escaneos</span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <EmptyState icon={<Trophy />} title="Sin productos destacados" />
                    )}
                    {analytics.segmentMix?.length > 0 && (
                        <div className="px-5 py-4 border-t border-surface-100">
                            <div className="mb-2 text-xs font-medium text-surface-500">Mix por segmento</div>
                            <div className="flex flex-wrap gap-1.5">
                                {analytics.segmentMix.map((s) => (
                                    <StatusTag key={s.segment} tone="brand">
                                        {s.segment}: <span className="tabular-nums">{fmtNum(s.scans)}</span>
                                    </StatusTag>
                                ))}
                            </div>
                        </div>
                    )}
                </SectionCard>
            </div>

            <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
                <SectionCard title="Visitas recientes" icon={<Store />} flush>
                    <ActivityFeed visits={analytics.recentStoreChecks} onVisitClick={onVisitClick} />
                </SectionCard>
                <SectionCard title="Auditores más activos (30d)" icon={<Users />} flush>
                    <AuditorRankings auditors={analytics.topAuditorsLast30Days} onAuditorClick={onAuditorClick} />
                </SectionCard>
            </div>
        </div>
    );
}
